import {
  createSignal,
  onMount,
  onCleanup,
  splitProps,
  children,
  createEffect,
  type JSX,
  type Component,
} from 'solid-js';

import type { PagiflowInstance, PagiflowOptions } from './types';

function getPagiflow(): ((selector: string | HTMLElement, opts?: PagiflowOptions) => PagiflowInstance) | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow;
  }
  try {
    const mod = require('pagiflow');
    return mod.default ?? mod;
  } catch {
    return null;
  }
}

export interface PagiflowProps extends PagiflowOptions {
  /** Slide content */
  children?: JSX.Element;
  /** CSS class on the track element */
  class?: string;
  /** Inline style on the track element */
  style?: JSX.CSSProperties | string;
  /** ID on the track element */
  id?: string;
  /** Called when the active slide changes (0-based real index) */
  onSlideChange?: (index: number) => void;
  /**
   * Render prop for custom navigation.
   * Receives { next, prev, currentIndex, instance }.
   */
  renderNav?: (props: {
    next: () => void;
    prev: () => void;
    currentIndex: number;
    instance: PagiflowInstance | null;
  }) => JSX.Element;
  /**
   * Render prop for custom pagination.
   * Receives { goTo, currentIndex, total, instance }.
   */
  renderPagination?: (props: {
    goTo: (index: number, opts?: { silent?: boolean; instant?: boolean }) => void;
    currentIndex: number;
    total: number;
    instance: PagiflowInstance | null;
  }) => JSX.Element;
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * Drop-in SolidJS component wrapping the Pagiflow core.
 *
 * @example
 * <Pagiflow loop nav onSlideChange={setIdx}>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </Pagiflow>
 */
export const Pagiflow: Component<PagiflowProps> = (props) => {
  // Split component-specific props away from PagiflowOptions
  const [local, options] = splitProps(props, [
    'children',
    'class',
    'style',
    'id',
    'onSlideChange',
    'renderNav',
    'renderPagination',
  ]);

  let trackEl!: HTMLDivElement;
  let instance: PagiflowInstance | null = null;

  const [currentIndex, setCurrentIndex] = createSignal(
    (options as PagiflowOptions).startIndex ?? 0
  );

  // ─── Init ──────────────────────────────────────────────────────────────────
  onMount(() => {
    const existing = (trackEl as any).pagiflowInstance as PagiflowInstance | undefined;
    if (existing && !existing.destroyed) return;

    const PagiflowCtor = getPagiflow();
    if (!PagiflowCtor) {
      console.error('[Pagiflow] Core not found. Install pagiflow or load it via CDN.');
      return;
    }

    const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
    const selector = `[data-pagiflow-solid-id="${attrValue}"]`;
    trackEl.setAttribute('data-pagiflow-solid-id', attrValue);

    // options contains all PagiflowOptions after splitProps
    const inst = PagiflowCtor(selector, options as PagiflowOptions) as PagiflowInstance | null;
    if (!inst) {
      trackEl.removeAttribute('data-pagiflow-solid-id');
      return;
    }

    const syncLayout = () => { (inst as any).update?.(); };

    const raf1 = requestAnimationFrame(() => syncLayout());
    const raf2 = requestAnimationFrame(() => syncLayout());
    const timeoutId = setTimeout(() => syncLayout(), 80);
    window.addEventListener('load', syncLayout, { once: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => syncLayout());
      resizeObserver.observe(trackEl);
    }

    inst.onSlideChange((idx) => {
      setCurrentIndex(idx);
      local.onSlideChange?.(idx);
    });

    instance = inst;

    onCleanup(() => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timeoutId);
      window.removeEventListener('load', syncLayout);
      resizeObserver?.disconnect();
      if (instance && !instance.destroyed) {
        try { instance.destroy(); } catch { /* ignore */ }
      }
      delete (trackEl as any).pagiflowInstance;
      instance = null;
    });
  });

  // Keep core options in sync when Solid props change.
  createEffect(() => {
    if (!instance || instance.destroyed) return;
    const latestOptions = options as PagiflowOptions;
    instance.setOptions({ ...latestOptions }, true);
  });

  // ─── Stable API ────────────────────────────────────────────────────────────
  const next = () => instance?.next();
  const prev = () => instance?.prev();
  const goTo = (index: number, opts?: { silent?: boolean; instant?: boolean }) =>
    instance?.goTo(index, opts);

  // Resolve children to count slides for renderPagination
  const resolvedChildren = children(() => local.children);
  const slideCount = () => {
    const c = resolvedChildren();
    return Array.isArray(c) ? c.length : c ? 1 : 0;
  };

  return (
    <div class="pagiflow-solid-wrapper" style={{ display: 'contents' }}>
      <div
        ref={trackEl}
        id={local.id}
        class={['pagiflow', local.class].filter(Boolean).join(' ')}
        style={local.style as string | undefined}
      >
        {resolvedChildren()}
      </div>

      {local.renderNav?.({
        next,
        prev,
        get currentIndex() { return currentIndex(); },
        get instance() { return instance; },
      })}

      {local.renderPagination?.({
        goTo,
        get currentIndex() { return currentIndex(); },
        get total() { return slideCount(); },
        get instance() { return instance; },
      })}
    </div>
  );
};
